/** @flow */
import { update } from "@store/utils";
import * as actions from "../constants/actions";
import type {
  MedicalTriageType,
  SettingTheSceneType,
  MedicationType,
  AllergyType,
  PastMedicalHistoryType,
  AssessmentOutcomePartType,
  LifestyleAdviceType,
  SymptomAdviceType,
  ClassificationType,
} from "../types";
import { cloneObject } from "@utils/object";

export type MedicalTriageState = {
  triage: ?MedicalTriageType,
  settingTheScene: ?SettingTheSceneType,
  error: ?string,
  medicalHistory: {
    content: Array<PastMedicalHistoryType>,
  },
  medications: {
    content: Array<MedicationType>,
  },
  allergies: {
    content: Array<AllergyType>,
  },
  assessment: {
    outcome: ?AssessmentOutcomePartType,
    symptoms: ?Array<SymptomAdviceType>,
  },
  advice: {
    lifestyle: {
      lifestyleAdviceGiven: Array<LifestyleAdviceType>,
    },
  },
  classification: {
    content: ?ClassificationType,
    schema: ?Object,
  },
};

export const initialState: MedicalTriageState = {
  triage: null,
  error: null,
  settingTheScene: null,
  medicalHistory: {
    content: [],
  },
  medications: {
    content: [],
  },
  allergies: {
    content: [],
  },
  assessment: {
    outcome: null,
    symptoms: null,
  },
  advice: {
    lifestyle: {
      lifestyleAdviceGiven: [],
    },
  },
  classification: {
    content: null,
    schema: null,
  },
};

const setError = (state: MedicalTriageState) => update(state)("error");

const onCreateMedicalTriage = (state: MedicalTriageState) => () => ({
  ...state,
  triage: null,
});

const onCreateMedicalTriageSuccess = (state: MedicalTriageState) => ({ payload }: *) => ({
  ...state,
  triage: payload,
  settingTheScene: null,
});

const onUpdateMedicalTriage = (state: MedicalTriageState) => () => ({
  ...state,
  triage: null,
});

const onUpdateMedicalTriageSuccess = (state: MedicalTriageState) => ({ payload }: *) => ({
  ...state,
  triage: payload,
});

const onGetPastMedicalHistory = (state: MedicalTriageState) => () => ({
  ...state,
  medicalHistory: {
    ...state.medicalHistory,
    content: [],
  },
});

const onGetPastMedicalHistorySuccess = (state: MedicalTriageState) => ({ payload }: *) => ({
  ...state,
  medicalHistory: payload,
});

const onGetCurrentMedications = (state: MedicalTriageState) => () => ({
  ...state,
  medications: {
    ...state.medications,
    content: [],
  },
});

const onGetCurrentMedicationsSuccess = (state: MedicalTriageState) => ({ payload }: *) => ({
  ...state,
  medications: payload,
});

const onGetCurrentAllergies = (state: MedicalTriageState) => () => ({
  ...state,
  allergies: {
    ...state.allergies,
    content: [],
  },
});

const onGetCurrentAllergiesSuccess = (state: MedicalTriageState) => ({ payload }) => ({
  ...state,
  allergies: payload,
});

const onCreateAssessmentOutcome = (state: MedicalTriageState) => () => ({
  ...state,
  assessment: {
    ...state.assessment,
    content: null,
  },
});

const onCreateAssessmentOutcomeSuccess = (state: MedicalTriageState) => ({ payload }: *) => ({
  ...state,
  assessment: payload,
});

const onGetLifestyleAdvice = (state: MedicalTriageState) => () => ({
  ...state,
  advice: {
    ...state.advice,
    lifestyle: {
      ...state.advice.lifestyle,
      lifestyleAdviceGiven: [],
    },
  },
});

const onGetLifestyleAdviceSuccess = (state: MedicalTriageState) => ({ payload }) => {
  return {
    ...state,
    advice: {
      ...state.advice,
      lifestyle: payload,
    },
  };
};

const onSetMedicalTriageClassification = (state: MedicalTriageState) => ({ payload }: Object) => {
  return {
    ...state,
    classification: {
      ...state.classification,
      content: payload,
    },
  };
};

const onSetMedicalTriageClassificationSchema = (state: MedicalTriageState) => ({
  payload,
}: Object) => ({
  ...state,
  classification: {
    ...state.classification,
    schema: payload,
  },
});

const onPostSettingTheSceneTextSuccess = (state: MedicalTriageState) => ({ payload }: Object) => ({
  ...state,
  settingTheScene: payload,
});

export default (state: MedicalTriageState = initialState, action: Object): MedicalTriageState => {
  const act = {
    [actions.UpdateMedicalTriage]: onUpdateMedicalTriage,
    [actions.UpdateMedicalTriageSuccess]: onUpdateMedicalTriageSuccess,
    [actions.UpdateMedicalTriageError]: setError,
    [actions.CreateMedicalTriage]: onCreateMedicalTriage,
    [actions.CreateMedicalTriageSuccess]: onCreateMedicalTriageSuccess,
    [actions.CreateMedicalTriageError]: setError,
    [actions.GetPastMedicalHistory]: onGetPastMedicalHistory,
    [actions.GetPastMedicalHistorySuccess]: onGetPastMedicalHistorySuccess,
    [actions.GetPastMedicalHistoryError]: setError,
    [actions.GetCurrentMedications]: onGetCurrentMedications,
    [actions.GetCurrentMedicationsSuccess]: onGetCurrentMedicationsSuccess,
    [actions.GetCurrentMedicationsError]: setError,
    [actions.GetCurrentAllergies]: onGetCurrentAllergies,
    [actions.GetCurrentAllergiesSuccess]: onGetCurrentAllergiesSuccess,
    [actions.GetCurrentAllergiesError]: setError,
    [actions.CreateAssessmentOutcome]: onCreateAssessmentOutcome,
    [actions.CreateAssessmentOutcomeSuccess]: onCreateAssessmentOutcomeSuccess,
    [actions.CreateAssessmentOutcomeError]: setError,
    [actions.SubmitAssessmentOutcomeSuccess]: onCreateAssessmentOutcomeSuccess,
    [actions.SubmitAssessmentOutcomeError]: setError,
    [actions.GetMedicalAssessmentSuccess]: onCreateAssessmentOutcomeSuccess,
    [actions.GetMedicalAssessmentError]: setError,
    [actions.GetLifestyleAdvice]: onGetLifestyleAdvice,
    [actions.GetLifestyleAdviceSuccess]: onGetLifestyleAdviceSuccess,
    [actions.GetLifestyleAdviceError]: setError,
    [actions.GetMedicalTriageClassificationSuccess]: onSetMedicalTriageClassification,
    [actions.GetMedicalTriageClassificationError]: setError,
    [actions.UpdateMedicalTriageClassification]: onSetMedicalTriageClassification,
    [actions.GetMedicalTriageClassificationSchemaSuccess]: onSetMedicalTriageClassificationSchema,
    [actions.GetMedicalTriageClassificationSchemaError]: setError,
    [actions.SubmitMedicalTriageClassificationSuccess]: onSetMedicalTriageClassification,
    [actions.SubmitMedicalTriageClassificationError]: setError,
    [actions.PostSettingTheSceneTextSuccess]: onPostSettingTheSceneTextSuccess,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
